import { authedFetch, UnauthorizedError } from '@apis/auth';
import type { AiBriefSectionKey, BriefSectionKey } from '@constants/briefSections';
import type { AiCheck } from '@d-orca/shared/ai-answer';
import type { AiLink } from '@d-orca/shared/navigation';
import { AiRateLimitedError, AiTurnInProgressError } from './aiAgentErrors';
// The wire — the event union, the frame decoder and the read loop — lives beside
// this file (`aiAgentStream.ts`). Re-exported so every existing consumer of
// `AiChatStreamEvent` keeps its import.
import { readSseStream, type AiBriefProposal, type AiChatStreamEvent } from './aiAgentStream';

export type { AiBriefProposal, AiChatStreamEvent };

export { AiRateLimitedError, AiTurnInProgressError };

/**
 * AI agent BFF — frontend mirrors of the response shapes served by
 * `/api/sites/:siteId/ai-agent/...`. Conversation list / detail / chat
 * are scoped per site so tanstack-query keys naturally invalidate on
 * site switch (matching how monitor / analytics behave).
 *
 * The assistant is NATIVE to d-orca (ADR-0011): d-orca owns the model
 * call, the tools, the prompt and the conversation store. Nothing here
 * proxies Trusstor.
 *
 * Chat is SSE-only and the answer STREAMS: `text` frames are appended as the
 * model writes them. Which text is the answer is the backend's decision — the
 * model delivers it through a tool of its own, so its narration never reaches
 * the wire — and `status` frames carry the tool calls in the meantime.
 */

export type AiMessageRole = 'user' | 'assistant';

/** A reader's verdict on one assistant answer. `null` on the wire clears it. */
export type AiMessageFeedback = 'up' | 'down';

export interface AiTableColumn {
  id: string;
  label: string;
  type: string;
}

/**
 * Cells are `unknown` — rich column types (`badge_list`, `progress`,
 * `trend`, `profession`) emit non-primitive values. The table renderer
 * narrows per column.type with a string fallback.
 */
export type AiTableCell = unknown;

export interface AiTable {
  title: string;
  columns: AiTableColumn[];
  rows: Record<string, AiTableCell>[];
  /** `forecast` rows are drawn as day cards (`ForecastStrip`) rather than a grid. */
  view?: 'forecast';
  /** Column ids the reader may group the rows by; `groupBy` is the one shown first. Set only on breakdowns. */
  groupings?: string[];
  groupBy?: string;
}

/**
 * A chart, in the shape `AiChart.tsx` draws with ApexCharts. The type set is
 * closed because that component switches on it — and it is the model's OPENING
 * choice rather than a fixed one, since the renderer offers a switcher.
 */
export type AiChartType = 'bar' | 'line' | 'area' | 'pie' | 'donut';

export interface AiChartSeries {
  name: string;
  /** One value per label, in the same order. */
  /** `null` is a gap in the line, not a zero — a period with no rate. */
  data: (number | null)[];
}

export interface AiChart {
  chartType: AiChartType;
  title: string;
  /** Categories: the x axis for bar/line/area, the slices for pie/donut. */
  labels: string[];
  series: AiChartSeries[];
}

export interface AiMessage {
  /**
   * Stable server id. Feedback is addressed by this and never by position:
   * the stored `messages` array and the rendered one are not guaranteed to
   * line up, so an index means one message on the client and possibly
   * another on the server.
   */
  id: string;
  role: AiMessageRole;
  content: string;
  /**
   * Absent until the reader rates it, and only ever set on assistant
   * messages. Comes back on the conversation-detail read so a reopened
   * thread shows the thumb the user already gave.
   */
  feedback?: AiMessageFeedback;
  /**
   * EVERY drawing the answer made, in order — appended from the live
   * `table`/`chart` frames, and read back from the store when a thread is
   * reopened. Both were once live-only, which meant a reopened thread showed
   * prose referring to a table that was not there.
   *
   * It used to be at most one of each, which broke the moment an answer drew
   * twice: the morning brief draws a table per section, and a reopened
   * briefing read "the main unreported tasks:" followed by nothing.
   */
  drawings?: AiDrawing[];
  /**
   * The links the answer offered, in order — appended from live `link` frames
   * and read back with the thread. Intent, not URLs: the href is built from
   * `@d-orca/shared/navigation` when the reader clicks.
   */
  links?: AiLink[];
  /**
   * When the message was written (ISO). The answer receipt's time on a
   * reopened thread; live, the page stamps the `done` frame's arrival instead —
   * at minute precision the two cannot be told apart.
   */
  at?: string;
  /**
   * What the answer checked, source by source — its receipt. Typed as this
   * build reads it, but stored for 90 days by builds that may have known other
   * sources: read through `checksOf`, which drops what no longer parses.
   */
  checks?: AiCheck[];
  /** The next questions suggested after the answer (shown above the composer). Read through `followUpsOf`, for the same reason. */
  followUps?: string[];
  /** Pre-`drawings` threads. Rendered, never written. */
  table?: AiTable;
  chart?: AiChart;
}

/**
 * One thing the assistant put on screen, tagged so order survives storage.
 * `id` is what its marker in the answer names (`@d-orca/shared/ai-answer`);
 * a drawing stored before markers existed has none and follows the prose.
 */
export type AiDrawing =
  | { kind: 'table'; table: AiTable; id?: string }
  | { kind: 'chart'; chart: AiChart; id?: string }
  | { kind: 'image'; image: AiGuideImage; id?: string };

/**
 * A Help Center screenshot the answer shows beside a step. `src` is the
 * backend's authed asset path (`/api/help/assets/…`), prefixed with the BFF
 * origin when drawn, exactly as the Help Center draws it.
 */
export interface AiGuideImage {
  src: string;
  /** Where it is from: "Scheduler › Adding a task". */
  caption: string;
  width?: number;
  height?: number;
}

export interface AiConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  /**
   * The line of the thread a search matched, so a hit can be picked without
   * being opened. Present ONLY on a search result — an unfiltered row has no
   * hit to show.
   */
  snippet?: string;
}

export interface AiConversationDetail {
  id: string;
  title: string;
  messages: AiMessage[];
}


/**
 * Turns a failed response into the error this surface throws. Exported for
 * `aiAgentReport.ts`, which talks to the same routes' neighbours and must fail
 * the same way.
 */
export async function handle(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  if (res.status === 401) throw new UnauthorizedError();
  if (res.status === 409 && body?.error?.code === 'AI_TURN_IN_PROGRESS') {
    throw new AiTurnInProgressError();
  }
  // Any 429, whatever its body: the limiter answers before the route does, so
  // its body is the limiter's, not ours.
  if (res.status === 429) throw new AiRateLimitedError();
  throw new Error(`request failed: ${res.status} ${body?.error?.message ?? ''}`);
}

export interface IListAiConversationsOptions {
  /**
   * Free-text filter over the caller's own threads — title and message text.
   * Empty or absent means the whole list, newest first: search is a filter on
   * the rail, not a separate mode, so clearing the box is a normal read.
   */
  q?: string;
  signal?: AbortSignal;
}

export async function listAiConversations(
  siteId: string,
  opts: IListAiConversationsOptions = {},
): Promise<AiConversationSummary[]> {
  // The query is omitted rather than sent empty: `?q=` and no `q` mean the
  // same thing to the backend, and one URL per meaning keeps the cache honest.
  const query = opts.q ? `?q=${encodeURIComponent(opts.q)}` : '';
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/ai-agent/conversations${query}`,
    { signal: opts.signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as AiConversationSummary[];
}

/**
 * Rename one thread. Answers with the stored summary, so the rail can settle
 * on what the server kept rather than on what it hoped for.
 *
 * 404 covers every reason it did not happen — foreign thread, no such thread —
 * so any rejection here means the optimistic row must be rolled back.
 */
export async function renameAiConversation(
  siteId: string,
  conversationId: string,
  title: string,
): Promise<AiConversationSummary> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/ai-agent/conversations/${encodeURIComponent(conversationId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as AiConversationSummary;
}

export async function getAiConversation(
  siteId: string,
  conversationId: string,
  signal?: AbortSignal,
): Promise<AiConversationDetail | null> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/ai-agent/conversations/${encodeURIComponent(conversationId)}`,
    { signal },
  );
  if (res.status === 404) return null;
  if (!res.ok) return handle(res);
  return (await res.json()) as AiConversationDetail;
}

export async function deleteAiConversation(
  siteId: string,
  conversationId: string,
): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/ai-agent/conversations/${encodeURIComponent(conversationId)}`,
    { method: 'DELETE' },
  );
  // `res.ok` is true for any 2xx including the backend's 204 No Content,
  // so we don't need to special-case the status code.
  if (!res.ok) return handle(res);
}

/**
 * Rate one assistant message, or clear the rating with `null`.
 *
 * Addressed by the message's own id — `AiMessage.id`, or the `answerId` the
 * turn's `metadata` frame carried. The backend answers 404 for everything
 * that did not happen (foreign thread, no such thread, no such message, or
 * an id naming the user's own question), so any rejection here means the
 * optimistic update must be rolled back.
 */
export async function setAiMessageFeedback(
  siteId: string,
  conversationId: string,
  messageId: string,
  rating: AiMessageFeedback | null,
): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/ai-agent/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/feedback`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rating }),
    },
  );
  // 204 No Content — `res.ok` covers it.
  if (!res.ok) return handle(res);
}

/**
 * Open the SSE chat stream and invoke `onEvent` for each normalised
 * event. Resolves when the stream ends naturally (server emits `done`
 * or closes the connection). Rejects on network / non-2xx initial
 * response. `signal` lets the caller abort mid-stream (e.g. user
 * clicked New Chat while a reply was generating).
 */
/**
 * Where in this morning's brief a question came from.
 *
 * A REFERENCE, never the brief's text: the server reads that reader's own
 * insight and the section's rows and puts them before the question, so the
 * model answering knows what the question was written from.
 */
export interface AiBriefRef {
  section: AiBriefSectionKey;
  /** Which insight of the reading, by position. Absent for a section's own question. */
  insight?: number;
}

export async function streamAiChat(
  siteId: string,
  query: string,
  conversationId: string | null,
  onEvent: (event: AiChatStreamEvent) => void,
  signal?: AbortSignal,
  briefRef?: AiBriefRef,
): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/ai-agent/chat/stream`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        query,
        ...(conversationId ? { conversationId } : {}),
        ...(briefRef ? { briefRef } : {}),
      }),
      signal,
    },
  );
  if (!res.ok || !res.body) return handle(res);
  return readSseStream(res.body, onEvent);
}



/**
 * One section of the morning brief.
 *
 * NUMBERS, NOT SENTENCES. `facts` carries the counts the summary is made of and
 * the UI writes the sentence, which is the only way one brief reads correctly in
 * two languages — the assistant's own prose could only ever be in one.
 *
 * `state` is the honesty contract, and the page must key off it before it looks
 * at a number: `none` is a confirmed zero, while `unavailable` and `unconfirmed`
 * both mean nobody looked. Rendering either of them as "nothing to report" is the
 * failure the whole brief is written against.
 */
export interface AiBriefSection {
  key: AiBriefSectionKey;
  state: 'rows' | 'none' | 'unavailable' | 'unconfirmed';
  facts: Record<string, number>;
  table: AiTable | null;
}

/**
 * One thing worth the reader's attention, with the section it rests on.
 *
 * The sections are query results — a report could produce every line of them.
 * These are what the model is actually for: which one deserves the first ten
 * minutes, what two of them say together, and the question to ask next. It
 * never travels without naming its section, so a claim with no evidence behind
 * it cannot be rendered.
 */
export interface AiBriefInsight {
  section: AiBriefSection['key'];
  /** critical: can stop work or needs action today; warning: needs follow-up; info: worth knowing. */
  severity: 'critical' | 'warning' | 'info';
  headline: string;
  /** One sentence: what a reader gets without asking for more. */
  why: string;
  /** The specifics, behind a click. Absent when the sentence carries it all. */
  detail?: string;
  /** A question for the assistant — the brief itself cannot answer it. */
  ask: string;
}

export interface AiBrief {
  /** The site's own local day this brief covers. */
  date: string;
  sections: AiBriefSection[];
}

/**
 * The reader's own insights on today's brief — the dashboard card and the
 * sheet's lede read the same thing.
 */
/**
 * What the reader is told about their standing instruction. Every field empty
 * means all is well. The settings dialog gets it before saving (the check); the
 * insights carry it, with the model's note, every morning after.
 */
export interface AiBriefInstructionStatus {
  /** What it asks that the brief cannot show, in the reader's own words. */
  unsupported: string[];
  /** Sections it is about that the reader has muted. */
  mutedConflicts: BriefSectionKey[];
  /** The floor it names, when the site has no such storey. */
  unknownFloor: string | null;
  /** The last day it applied, once passed (`YYYY-MM-DD`). */
  expiredOn: string | null;
  /** The day it starts, while still ahead (`YYYY-MM-DD`). */
  startsOn: string | null;
}

export interface AiBriefInsights {
  /** The site's own local day they are about. */
  date: string;
  /**
   * The reader's instruction and how today's brief answered it — null for a
   * reader with none. `note` is the model's sentence; the rest is computed, so it
   * is said even when the model forgot to.
   */
  instruction: (AiBriefInstructionStatus & { note: string }) | null;
  /** The state of the site this morning, in a few sentences. Empty when none was usable. */
  overview: string;
  /** The same status at length, behind a press. Empty when the model wrote none. */
  overviewDetail: string;
  /** What deserves attention, most severe first. Empty when nothing does. */
  insights: AiBriefInsight[];
  /**
   * Sections this reader muted — the mutes the SERVER applied, so a surface shows
   * the same set the model was denied rather than whatever its own preferences
   * query happens to hold.
   *
   * It is rendered rather than ignored because a mute has to be VISIBLE: the
   * section's verdict and rows leave the page, which is what "stop telling me
   * about weather" asks for, but a muted safety line must never read as a
   * confirmed zero. `none` means the read ran and found nothing; a mute means
   * nobody showed you.
   */
  mutedSections: BriefSectionKey[];
  /** Each section's state, so a surface with no ledger can still mark the evidence. */
  sectionStates: Pick<AiBriefSection, 'key' | 'state'>[];
  /** When they were written. */
  generatedAt: string;
}

/**
 * The reader's insights for today. The first read of the day writes them — a
 * model call on the server — so this can take seconds where the brief takes
 * none; every later read is the server's cache.
 */
export async function fetchBriefInsights(
  siteId: string,
  signal?: AbortSignal,
): Promise<AiBriefInsights> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/ai-agent/brief/insights`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as AiBriefInsights;
}

/**
 * What the brief will make of an instruction, BEFORE it is saved — from the
 * brief's own compiler, so the dialog cannot promise what the brief then
 * ignores. A model call on the server, and rate-limited like a tuning turn.
 * Fast; `adds` says a preview (`previewBriefInstruction`) is worth asking for.
 */
export interface AiBriefInstructionCheck extends AiBriefInstructionStatus {
  adds: boolean;
}

/** The final status, and what the brief's summary would say about the draft today. */
export interface AiBriefInstructionPreview extends AiBriefInstructionStatus {
  /**
   * What the brief's summary would say about what the instruction adds, from
   * today's data — prose, as the summary is. Null when it adds nothing, or
   * nothing it adds could be read today.
   */
  preview: { summary: string } | null;
}

/** A draft instruction, posted to one of the dialog's two checks. */
async function postDraft<T>(
  siteId: string,
  path: 'check' | 'preview',
  draft: { interest: string; mutedSections: BriefSectionKey[] },
  signal?: AbortSignal,
): Promise<T> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/ai-agent/brief-tuning/${path}`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(draft), signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as T;
}

export function checkBriefInstruction(
  siteId: string,
  draft: { interest: string; mutedSections: BriefSectionKey[] },
  signal?: AbortSignal,
): Promise<AiBriefInstructionCheck> {
  return postDraft(siteId, 'check', draft, signal);
}

/** Slow — the assistant may look things up, and the summary is written. */
export function previewBriefInstruction(
  siteId: string,
  draft: { interest: string; mutedSections: BriefSectionKey[] },
  signal?: AbortSignal,
): Promise<AiBriefInstructionPreview> {
  return postDraft(siteId, 'preview', draft, signal);
}

/** Write the reader's insights again, replacing today's. Rate-limited on the server. */
export async function regenerateBriefInsights(siteId: string): Promise<AiBriefInsights> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/ai-agent/brief/insights/regenerate`,
    { method: 'POST' },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as AiBriefInsights;
}

/**
 * This morning's brief for a site.
 *
 * A miss in the server's cache is not an error — it rebuilds one from the same
 * reads — so this either answers with a brief or fails outright.
 */
export async function fetchMorningBrief(siteId: string, signal?: AbortSignal): Promise<AiBrief> {
  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/ai-agent/brief`, {
    signal,
  });
  if (!res.ok) return handle(res);
  return (await res.json()) as AiBrief;
}


/** One exchange of the tuning conversation. Nothing is stored, so it travels. */
export interface AiTuningMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Talk to the assistant about what the morning brief should be about.
 *
 * EPHEMERAL: no thread is stored server-side, so the exchange so far is sent with
 * each turn. The durable output is the preference, which the reader saves from the
 * proposal card.
 */
export async function streamBriefTuning(
  siteId: string,
  query: string,
  history: AiTuningMessage[],
  onEvent: (event: AiChatStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/ai-agent/brief-tuning/stream`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify({ query, history }),
      signal,
    },
  );
  if (!res.ok || !res.body) return handle(res);
  return readSseStream(res.body, onEvent);
}
